/**
 * The playback states, and how a stream is judged to be in one (`R-VID-12`, `R-VID-15`).
 *
 * **A clone of `apps/customer/src/room/playback.ts`, and deliberately a clone.** The panel is a
 * separate build with no import path into the customer application, the same way its `Lot` type
 * is hand-written against `lib/shows.ts`. Sharing it would mean moving this and the hook beside
 * it into `packages/ui`, which is a change to the room — the one surface in this product that
 * must not break — in service of a card in the panel. The copy is the cheaper risk, and what
 * keeps the two honest is that both live in this repository and `apps/host/test/playback.test.ts`
 * asserts the same behaviour as the room's.
 *
 * It is pure: no IVS SDK, no DOM, no timers. It takes a reading of what the browser can see and
 * answers with a state. Everything that is a judgement rather than a fact lives here so that it
 * can be tested against a clock we control.
 *
 * ## Why frames rather than events
 *
 * `R-VID-15` is the tightest coupling in the specification: *detection must complete within two
 * seconds of the stream stopping*, because an auction with eight seconds left would otherwise
 * close in the dark and `R-AUC-16` would never fire. Two seconds is not much, and the obvious
 * signal — IVS telling us the publisher left — is not the fast one. It is reliable when the host
 * closes a tab, and it depends on a server-side timeout when a phone loses signal in a lift,
 * which is the case that matters.
 *
 * So the primary signal is **media arriving**: a cumulative counter from the subscriber's own
 * WebRTC statistics, sampled several times a second — decoded frames where there is a picture, and
 * received audio packets where there is not (a host with no camera publishes a voice, which
 * `R-VID-7` explicitly prefers to nothing). A stream that has stopped stops advancing it, whatever
 * the reason and whatever IVS has noticed. The SDK's events are used as well, and where they fire
 * first they win — `streamPresent` below is one of them — but nothing depends on them arriving.
 *
 * ## Why the viewer's own connection is a different answer
 *
 * `R-VID-17`: *a viewer's own connection failing is a different condition from the host's stream
 * stopping, and the two are never conflated. Telling a customer the wrong one sends them to reset
 * a router that was working.* So `cause` is carried beside the state, and — this is the part worth
 * defending — there is a third value for **neither**. When frames simply stop arriving while our
 * connection to IVS is still healthy, the browser genuinely cannot tell whose fault it is. Naming
 * it `unknown` and saying "the stream has stopped" is the honest answer; guessing is how a
 * customer is sent to reset a router that was working.
 */

export type PlaybackState =
  /** Joining, or waiting for the first frame. The state a room opens in. */
  | "connecting"
  | "playing"
  /** Frames have paused briefly. Not yet an interruption — most of these recover unnoticed. */
  | "buffering"
  /** `R-VID-12` — the stream has stopped for long enough to say so. `cause` says whose. */
  | "interrupted"
  /** The show is over (`R-END-1` replaces the room entirely; this is the player's own state). */
  | "ended";

export type InterruptionCause =
  /** The publisher stopped: IVS said so, or their stream vanished after we had been playing. */
  | "host"
  /** We did: the browser is offline, or our own connection to the stage dropped. */
  | "viewer"
  /**
   * **A media path that could not be built.** IVS reported the subscription itself as errored —
   * which is what a failed ICE negotiation looks like from the SDK — so the show is fine, this
   * browser is connected to the stage, and no video can reach it.
   *
   * A fourth cause beside `R-VID-17`'s. Without it a viewer in this position — a Firefox viewer
   * is the known case — is told *the stream has stopped* and *it will come back on its own*, both
   * false: other viewers are watching, and it will not come back without something changing at
   * their end. The other causes cannot express *the connection was never made*, and calling it
   * `unknown` would be guessing — which is the one thing that requirement forbids.
   *
   * It is distinct from `viewer` because the advice is different. `viewer` is a connection that
   * dropped and will return; this is a connection that will not be made until an extension, a VPN
   * or a network that refuses UDP is dealt with.
   */
  | "blocked"
  /** Frames stopped and the browser cannot tell whose end failed. Never guessed at. */
  | "unknown";

/**
 * **600ms to buffering, 1,500ms to interrupted.**
 *
 * The second figure is what `R-VID-15` constrains, and it is set at 1,500 rather than 2,000 so
 * that the sampling interval fits *inside* the budget rather than being added to it: a stall is
 * noticed at worst one sample late, so 1,500 + a 400ms sample is still under two seconds. The
 * requirement is a ceiling on the whole detection, not on the threshold in isolation.
 *
 * The first figure exists because most stalls are not interruptions. A brief pause is what
 * `buffering` is for (`R-VID-12`), and showing "the stream has been interrupted" for every 700ms
 * hiccup would make the state that matters unreadable.
 */
export const BUFFERING_AFTER_MS = 600;
export const INTERRUPTED_AFTER_MS = 1_500;

/**
 * How long a viewer who arrives before the picture does is told "connecting" rather than
 * "interrupted".
 *
 * A show is only subscribed to while it is live, so a live show with no publisher *is* an
 * interruption — but joining takes a moment, and a viewer whose first two seconds say the host's
 * stream is broken has been told something false. Longer than `INTERRUPTED_AFTER_MS` because the
 * first frame has a join, an ICE negotiation and a keyframe ahead of it, where a later one has
 * nothing.
 */
export const FIRST_FRAME_GRACE_MS = 4_000;

/** Our own connection to the stage, as the SDK reports it. */
export type StageConnection = "connecting" | "connected" | "disconnected" | "errored";

export interface PlaybackSample {
  /** Milliseconds from a monotonic source. `performance.now()` in the browser. */
  at: number;
  connection: StageConnection;
  /** `navigator.onLine`. False is conclusive; true means nothing in particular. */
  online: boolean;
  /** Whether a publisher's video stream is currently attached to us. */
  streamPresent: boolean;
  /** IVS reporting the publisher's video as stopped or muted — an explicit, fast signal. */
  streamStopped: boolean;
  /**
   * IVS reporting **our subscription** to the publisher as errored.
   *
   * The other explicit signal, and the only one that says the failure is local while the show is
   * healthy: a subscribe that errors is a media path this browser could not build. Firefox says
   * `ICE failed, add a STUN server` in the console when it happens; Chromium says less.
   */
  subscribeFailed: boolean;
  /**
   * A cumulative counter that advances while media flows: decoded video frames, or received audio
   * packets on an audio-only stream. Only ever compared with its own previous value, so which of
   * the two it is does not matter — that it never goes backwards does.
   *
   * `undefined` before the first statistics report.
   */
  progress: number | undefined;
  /** The show's own state has moved to ended. Outranks everything below it. */
  showEnded: boolean;
}

/** What has to be remembered between samples. Two facts, both about time. */
export interface PlaybackMemory {
  /** The last sample at which media advanced, or at which we became connected. */
  lastProgressAt: number | undefined;
  lastProgress: number | undefined;
  everPlayed: boolean;
}

export interface PlaybackReading {
  state: PlaybackState;
  /** Only ever set when `state` is `interrupted`. */
  cause: InterruptionCause | undefined;
  memory: PlaybackMemory;
}

export const initialMemory: PlaybackMemory = {
  lastProgressAt: undefined,
  lastProgress: undefined,
  everPlayed: false,
};

/**
 * One sample in, one state out.
 *
 * The order of the checks is the specification's order of severity, and it is the whole of the
 * logic: the show being over outranks anything about a stream, our own connection outranks the
 * publisher's, and an explicit signal outranks a measured silence.
 */
export function readPlayback(
  memory: PlaybackMemory,
  sample: PlaybackSample,
): PlaybackReading {
  // Progress first, so that it is recorded even on a sample that ends up reporting something
  // else. A stream that is still delivering media is alive, whatever else is true.
  const advanced =
    sample.progress !== undefined &&
    memory.lastProgress !== undefined &&
    sample.progress > memory.lastProgress;

  const next: PlaybackMemory = {
    lastProgress: sample.progress ?? memory.lastProgress,
    lastProgressAt:
      advanced || memory.lastProgressAt === undefined
        ? sample.at
        : memory.lastProgressAt,
    everPlayed: memory.everPlayed || advanced,
  };

  const settle = (
    state: PlaybackState,
    cause?: InterruptionCause,
  ): PlaybackReading => ({
    state,
    cause: state === "interrupted" ? (cause ?? "unknown") : undefined,
    memory: next,
  });

  if (sample.showEnded) return settle("ended");

  // Conclusive, and ours. `navigator.onLine` is famously optimistic when true and reliable when
  // false, which is exactly the direction that is useful here (`R-VID-17`).
  if (!sample.online) return settle("interrupted", "viewer");

  /**
   * Also conclusive and also ours, and it outranks everything below it for that reason: IVS has
   * told us the subscription errored, so no amount of waiting on frame counters will produce a
   * better answer. Checked before the connection state because the stage session is typically
   * *connected* when this happens — the signalling worked and the media path did not, which is
   * exactly the case that would otherwise read as `unknown`.
   */
  if (sample.subscribeFailed) return settle("interrupted", "blocked");

  if (sample.connection === "disconnected" || sample.connection === "errored") {
    // Our session with the stage, not the publisher's — the host dropping does not disconnect
    // us. Before the first frame this is still a join in progress; after it, it is a loss.
    return next.everPlayed
      ? settle("interrupted", sample.connection === "errored" ? "unknown" : "viewer")
      : settle("connecting");
  }

  if (sample.connection === "connecting") return settle("connecting");

  // Connected. Everything from here is a statement about the publisher.
  if (sample.streamStopped) return settle("interrupted", "host");

  const silence = sample.at - (next.lastProgressAt ?? sample.at);

  if (!sample.streamPresent) {
    // Their stream is not attached to us. After we have seen it, that is the publisher leaving
    // and needs no waiting period — the event has already told us. Before, it is a host who has
    // not started yet, and a viewer is told "connecting" until the grace period runs out.
    if (next.everPlayed) return settle("interrupted", "host");
    return silence < FIRST_FRAME_GRACE_MS
      ? settle("connecting")
      : settle("interrupted", "host");
  }

  if (!next.everPlayed) {
    // Attached, but nothing decoded yet. The same grace period, for the same reason.
    return silence < FIRST_FRAME_GRACE_MS
      ? settle("connecting")
      : settle("interrupted", "unknown");
  }

  if (silence >= INTERRUPTED_AFTER_MS) return settle("interrupted", "unknown");
  if (silence >= BUFFERING_AFTER_MS) return settle("buffering");
  return settle("playing");
}
